/**
 * NetKeep Terms of Use and Privacy Policy, shared by the mobile and web apps so both
 * always show the same text. Plain data only: each app renders it with its own UI.
 *
 * Before launch:
 *   1. Fill in OWNER (anything still in [brackets] is shown to users as a draft warning).
 *   2. Have a lawyer licensed in your state review both documents.
 *   3. Bump LEGAL_EFFECTIVE_DATE whenever the text changes.
 *
 * The privacy policy describes what the code actually does. If you add analytics, crash
 * reporting, accounts, ads or anything that sends user data off the device, update it first.
 */

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface LegalDocument {
  slug: "terms" | "privacy" | "delete-data";
  title: string;
  effectiveDate: string;
  /** Short plain-language summary shown above the full text. */
  summary: string;
  sections: LegalSection[];
}

/** Who operates NetKeep. Values in [brackets] are placeholders that must be replaced. */
export const OWNER = {
  /** Your legal name, or your company's legal name (e.g. "NetKeep LLC"). */
  name: "Vou Xiong",
  /** Address that receives legal and privacy questions. */
  contactEmail: "xiongvou@gmail.com",
  /** U.S. state whose law governs the terms. */
  governingState: "Washington",
  /** County and state where disputes are heard. */
  venue: "Spokane County, Washington",
} as const;

/** Public page for data deletion requests (Google Play "Delete data URL"). Served by netkeep-web. */
export const DELETE_DATA_URL = "https://www.netkeeps.com/delete-data";

/** ISO date the current text took effect. */
export const LEGAL_EFFECTIVE_DATE = "2026-10-22";

/** OWNER fields still holding a [placeholder]. Empty means ready to publish. */
export function unfilledPlaceholders(): string[] {
  return Object.entries(OWNER)
    .filter(([, value]) => /\[[^\]]+\]/.test(value))
    .map(([key]) => key);
}

/** One line for the calculator results, next to the numbers people act on. */
export const SHORT_DISCLAIMER =
  "Estimates only, not tax or financial advice. Fees change; confirm with the platform before you price or sell.";

const owner = OWNER.name;

export const TERMS: LegalDocument = {
  slug: "terms",
  title: "Terms of Use",
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  summary:
    "NetKeep is a free estimating tool. Its numbers can be wrong or out of date, it is not professional advice, and you are responsible for the decisions you make with it.",
  sections: [
    {
      heading: "1. Agreement",
      paragraphs: [
        `These Terms of Use ("Terms") are an agreement between you and ${owner} ("we", "us"), the operator of NetKeep, including the NetKeep mobile app, the NetKeep website and the NetKeep fee-data service (together, the "Service").`,
        "By using the Service you agree to these Terms. If you do not agree, do not use the Service.",
      ],
    },
    {
      heading: "2. What NetKeep does",
      paragraphs: [
        "NetKeep estimates the fees, payout and profit of a sale from the numbers you enter and from fee schedules we maintain for selling platforms and payment methods.",
        "It is a planning and estimating tool only. It does not sell anything, process payments, connect to your platform accounts or know the actual fees on your account.",
      ],
    },
    {
      heading: "3. Estimates only — fees can be wrong or out of date",
      paragraphs: [
        "Platform fees vary by category, seller level, store subscription, region, promotions and other factors, and platforms can change them at any time without telling us. The fee schedules in NetKeep are compiled by hand from public sources and may be incomplete, simplified, unverified or out of date.",
        "Each fee profile shows a verification status and, where available, a link to its source. \"Unverified\" means no one has confirmed the rates against the platform's current published fees. Even \"Verified\" profiles can be wrong or become outdated.",
        "Results are estimates, rounded as described in the app. They are not a quote, invoice or statement of what any platform will charge you or pay you.",
      ],
    },
    {
      heading: "4. Not professional advice",
      paragraphs: [
        "NetKeep does not provide tax, legal, accounting, financial or business advice. Sales tax figures only show how tax may affect fees; they are not a determination of what tax you owe, collect or must remit. Consult a qualified professional for advice about your situation.",
      ],
    },
    {
      heading: "5. Your responsibility",
      paragraphs: ["You are solely responsible for:"],
      bullets: [
        "checking the current fees with each platform and payment provider before you list, price or sell;",
        "the accuracy of the numbers you enter;",
        "your pricing, listing, purchasing, tax and business decisions, even if you made them using NetKeep.",
      ],
    },
    {
      heading: "6. No warranty",
      paragraphs: [
        "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\", WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED OR STATUTORY, INCLUDING ANY WARRANTIES OF ACCURACY, COMPLETENESS, TIMELINESS, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT.",
        "We do not warrant that fee data or results are accurate, complete or current, or that the Service will be available, uninterrupted, secure or error-free. Some jurisdictions do not allow the exclusion of implied warranties, so some of these exclusions may not apply to you.",
      ],
    },
    {
      heading: "7. Limitation of liability",
      paragraphs: [
        `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${owner.toUpperCase()} AND ITS OWNERS, EMPLOYEES AND CONTRIBUTORS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, SALES, DATA OR GOODWILL, ARISING FROM OR RELATED TO THE SERVICE OR THESE TERMS, EVEN IF WE WERE TOLD SUCH DAMAGES WERE POSSIBLE.`,
        "TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICE IS LIMITED TO THE GREATER OF (A) THE AMOUNT YOU PAID US FOR THE SERVICE IN THE 12 MONTHS BEFORE THE CLAIM, OR (B) US$50.",
        "These limits apply to every theory of liability, including contract, tort (including negligence), strict liability and statute. Some jurisdictions do not allow these limitations, so they may not fully apply to you.",
      ],
    },
    {
      heading: "8. Other platforms and trademarks",
      paragraphs: [
        "NetKeep is independent. It is not affiliated with, endorsed by or sponsored by eBay, Mercari, Etsy, PayPal, Meta, or any marketplace or payment provider. Their names and trademarks belong to their owners and are used only to identify whose fees an estimate refers to.",
        "Links to other websites are provided for convenience. We do not control those sites and are not responsible for their content, accuracy or practices.",
      ],
    },
    {
      heading: "9. Acceptable use",
      paragraphs: ["You agree not to:"],
      bullets: [
        "use the Service for anything unlawful;",
        "overload, disrupt or try to gain unauthorized access to the Service or its servers, including by bypassing rate limits;",
        "access the administrative parts of the Service without our permission;",
        "present NetKeep results to others as official fees of any platform.",
      ],
    },
    {
      heading: "11. Changes and availability",
      paragraphs: [
        "We may change, suspend or stop the Service, or any feature or fee profile, at any time. We may update these Terms; the effective date at the top will change, and continuing to use the Service after an update means you accept the updated Terms.",
      ],
    },
    {
      heading: "11. Governing law and disputes",
      paragraphs: [
        `These Terms are governed by the laws of the State of ${OWNER.governingState}, USA, without regard to its conflict-of-laws rules. Any dispute related to the Service or these Terms will be brought only in the state or federal courts located in ${OWNER.venue}, and you and we consent to their jurisdiction. Nothing in these Terms limits rights you have under consumer-protection laws that cannot be waived by contract.`,
      ],
    },
    {
      heading: "12. General",
      paragraphs: [
        "If any part of these Terms is found unenforceable, the rest remains in effect. Our not enforcing a provision is not a waiver of it. These Terms, together with the Privacy Policy, are the entire agreement between you and us about the Service.",
      ],
    },
    {
      heading: "13. Contact",
      paragraphs: [`Questions about these Terms: ${OWNER.contactEmail}.`],
    },
  ],
};

export const PRIVACY: LegalDocument = {
  slug: "privacy",
  title: "Privacy Policy",
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  summary:
    "No account and no ads. Your calculations and the amounts you type stay on your device. We collect anonymous usage analytics (which screens and features are used), which you can turn off in Settings. Our server receives requests for fee data, which include your IP address.",
  sections: [
    {
      heading: "1. Who we are",
      paragraphs: [
        `This policy explains how ${owner} ("we", "us") handles information in the NetKeep mobile app, the NetKeep website and the NetKeep fee-data service (the "Service").`,
      ],
    },
    {
      heading: "2. Information that stays on your device",
      paragraphs: [
        "Everything you type into NetKeep — sale prices, costs, fees, tax settings — is calculated on your device and is never sent to us. The following is stored only on your device (in the app's local database, or in your browser's local storage on the website):",
      ],
      bullets: [
        "calculations you choose to save;",
        "a copy of the most recent fee data, so the app works offline;",
        "your settings, such as the color theme and whether usage analytics are on.",
      ],
    },
    {
      heading: "3. Information our server receives",
      paragraphs: [
        "To download current fee data, the app contacts the NetKeep server. Like any web request, this sends technical information: your IP address, the date and time, the address requested, and your device's browser or app identifier (user agent).",
        "We use this information only to deliver fee data, to protect the Service from abuse (for example, rate limiting by IP address), and to diagnose problems. It is not linked to your name or to any calculations, and we do not use it to build a profile of you. Server logs are kept by our hosting provider for a limited period and then deleted.",
      ],
    },
    {
      heading: "4. Usage analytics",
      paragraphs: [
        "To learn which features people use and how to improve NetKeep, the mobile app and the website send anonymous usage events to our analytics provider, PostHog. We collect:",
      ],
      bullets: [
        "which screens or pages are opened, and when the mobile app is opened, installed, updated or moved to the background;",
        "a fixed list of actions: choosing a selling platform (the platform, payment method and transaction type), saving a calculation, opening a saved calculation, checking for fee updates (and whether it worked), and changing the color theme;",
        "the app version and basic device information: operating system, device type, browser, screen size and language;",
        "a random identifier created on your device, stored in the app's storage or your browser's local storage (not a cookie), so we can count unique users without knowing who they are.",
      ],
    },
    {
      heading: "5. Limits on analytics, and turning them off",
      paragraphs: [
        "Analytics never include the amounts you type (prices, costs, fees or tax), your saved calculations, your name or contact details. We do not record your screen or what you tap or type.",
        "Like any web request, sending events reveals your IP address to PostHog, which may use it to estimate an approximate location such as your country or city. PostHog keeps analytics data for the retention period of our plan (currently at least one year) and then deletes it.",
        "You can turn analytics off at any time in Settings (\"Share anonymous usage data\"). On the website, analytics start turned off if your browser sends a Do Not Track or Global Privacy Control signal.",
      ],
    },
    {
      heading: "6. What we do not do",
      paragraphs: ["We do not:"],
      bullets: [
        "require an account, or collect your name, email address, phone number or payment details;",
        "use advertising, advertising identifiers, tracking pixels or cross-site tracking;",
        "set cookies on the website;",
        "sell or share your personal information, as \"sell\" and \"share\" are defined in U.S. state privacy laws such as the California Consumer Privacy Act.",
      ],
    },
    {
      heading: "7. Service providers",
      paragraphs: [
        "Our server and website are hosted by a cloud provider (currently Render), which processes the request information above on our behalf. Usage analytics are processed on our behalf by PostHog, Inc., on servers in the United States. App stores such as Google Play and the Apple App Store, and your device or browser, collect information under their own privacy policies, which we do not control.",
        "We may disclose information if required by law, or to protect the rights and safety of users, the public or the Service.",
      ],
    },
    {
      heading: "8. Your choices",
      paragraphs: [],
      bullets: [
        "Turn usage analytics off or on at any time in Settings.",
        `Ask us to delete your analytics data: see ${DELETE_DATA_URL}.`,
        "Delete saved calculations at any time in Settings (one at a time on the Saved screen, or all at once).",
        "On the website, clearing this site's data in your browser removes everything NetKeep stored there, including the analytics identifier.",
        "Uninstalling the mobile app removes its data from your device.",
        "We do not hold your calculations, and analytics are tied only to a random identifier, not to your name. You can still contact us with any privacy request, including a request to delete analytics data.",
      ],
    },
    {
      heading: "9. Security",
      paragraphs: [
        "Fee data and analytics are sent over HTTPS. Data stored on your device is protected by your device's own security; anyone with access to your unlocked device or browser profile can see your saved calculations.",
      ],
    },
    {
      heading: "10. Children",
      paragraphs: [
        "NetKeep is not directed to children under 13, and we do not knowingly collect personal information from children.",
      ],
    },
    {
      heading: "11. Changes",
      paragraphs: [
        "If we change how the Service handles information, we will update this policy and its effective date before the change takes effect.",
      ],
    },
    {
      heading: "12. Contact",
      paragraphs: [`Privacy questions or requests: ${OWNER.contactEmail}.`],
    },
  ],
};

export const DATA_DELETION: LegalDocument = {
  slug: "delete-data",
  title: "Delete your data",
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  summary:
    "NetKeeps has no accounts. Almost everything lives only on your device, and you can delete it yourself. To delete the anonymous usage data we hold, email us your analytics ID.",
  sections: [
    {
      heading: "Who this page is for",
      paragraphs: [
        `This page covers the NetKeeps Android app (com.netkeeps.app) and the NetKeeps website (www.netkeeps.com), operated by ${owner}. See the Privacy Policy for everything NetKeeps collects.`,
      ],
    },
    {
      heading: "1. Delete what is stored on your device",
      paragraphs: [
        "Your saved calculations, the downloaded fee data and your settings are stored only on your device. We never receive them, so you delete them yourself:",
      ],
      bullets: [
        "Saved calculations: Settings → \"Delete all saved calculations\", or delete them one at a time on the Saved screen.",
        "Everything in the Android app: Android Settings → Apps → NetKeeps → Storage → Clear storage, or uninstall the app.",
        "Everything on the website: clear this site's data (cookies and site data for netkeeps.com) in your browser settings.",
      ],
    },
    {
      heading: "2. Stop usage analytics",
      paragraphs: [
        "Turn off \"Share anonymous usage data\" in Settings (in the app and on the website separately). Nothing more is sent from that device. Clearing the app's storage or the site's data also discards your analytics ID, so any new data would not be linked to the old.",
      ],
    },
    {
      heading: "3. Ask us to delete your analytics data",
      paragraphs: [
        "Usage analytics are tied only to a random analytics ID, not to your name, so we need that ID to find your data:",
      ],
      bullets: [
        "Open Settings → Privacy and copy \"Your analytics ID\" (do this before clearing storage or turning analytics off).",
        `Email ${OWNER.contactEmail} with the subject "Delete my NetKeeps data" and include the ID. Send one ID per device or browser you used.`,
        "We delete all usage data linked to that ID from our analytics provider within 30 days and reply to confirm.",
      ],
    },
    {
      heading: "4. What is not covered",
      paragraphs: [
        "Our server's request logs (which include IP addresses, as described in the Privacy Policy) are not linked to you or to an analytics ID; our hosting provider deletes them automatically after a limited period. Data held by Google Play or your browser is governed by their own policies.",
      ],
    },
  ],
};

export const LEGAL_DOCUMENTS = [TERMS, PRIVACY, DATA_DELETION] as const;

// ---------------------------------------------------------------------------
// Usage analytics: the ONLY events the apps may send, as disclosed in PRIVACY
// sections 4 and 5. Both apps call sanitizeAnalyticsEvent() before sending, so
// nothing outside this list (and never an amount the user typed) can leave the device.
// Adding an event or property here means updating the Privacy Policy first.
// ---------------------------------------------------------------------------

export const ANALYTICS_EVENTS = {
  profile_selected: ["platform", "payment_method", "transaction_type"],
  calculation_saved: ["platform"],
  saved_calculation_opened: [],
  fee_data_refreshed: ["result"],
  theme_changed: ["theme"],
} as const;

export type AnalyticsEvent = keyof typeof ANALYTICS_EVENTS;
export type AnalyticsProps<E extends AnalyticsEvent> = Partial<
  Record<(typeof ANALYTICS_EVENTS)[E][number], string>
>;

/** Property values must be short labels or slugs (e.g. "ebay", "LOCAL_PICKUP"), never amounts. */
const SAFE_VALUE = /^[A-Za-z][A-Za-z_-]{0,39}$/;

/**
 * Keeps only allow-listed properties whose values are short letter-only labels.
 * Anything containing a digit (so any price, cost or fee) is dropped.
 */
export function sanitizeAnalyticsEvent<E extends AnalyticsEvent>(
  event: E,
  props: AnalyticsProps<E> = {},
): Record<string, string> {
  const allowed: readonly string[] = ANALYTICS_EVENTS[event];
  const clean: Record<string, string> = {};
  for (const [key, value] of Object.entries(props as Record<string, unknown>)) {
    if (allowed.includes(key) && typeof value === "string" && SAFE_VALUE.test(value)) clean[key] = value;
  }
  return clean;
}

/** Screen or page names sent with screen/page views: the route path only, no query string or hash. */
export function sanitizeScreenPath(path: string): string {
  const bare = path.split(/[?#]/)[0] ?? "/";
  return /^\/[A-Za-z0-9/_-]{0,80}$/.test(bare) ? bare : "/";
}
